import type { FormConfig } from '@risksmart-app/shared/forms/types';

import {
  isClosedSetDisplayType,
  SUPPORTED_DISPLAY_TYPES,
} from './coerceFieldValue';
import type { ActiveFormDescriptorField, DescriptorFieldOption } from './types';

/** Prefix under which custom attributes register as react-hook-form fields. */
const CUSTOM_ATTRIBUTE_PREFIX = 'CustomAttributeData';

/** One closed-option custom attribute, as resolved from the org's schema. */
export interface CustomAttributeOptionField {
  /** The attribute's own label — custom attributes are not in the form config. */
  label: string;
  /** True for `multiselect` (an array of values), false for `select`. */
  multiple: boolean;
  options: DescriptorFieldOption[];
}

/**
 * Where the closed option sets come from.
 *
 * Deliberately injected as plain functions rather than resolved in here: the
 * sources are React hooks bound to the org's i18n resources
 * (`useRating`/`useCommonLookupLazy`) and the form's custom-attribute schema, and
 * this function stays pure so it can be unit-tested without a provider tree.
 *
 * Every source is the SAME one the form's own control reads, which is what makes
 * the resulting set authoritative: it is exactly what the user could pick by
 * hand, resolved against their org's taxonomy overrides.
 */
export interface DescriptorOptionSources {
  /** Options for a `rating` field, by the form config's `ratingKey`. */
  getRatingOptions: (ratingKey: string) => DescriptorFieldOption[];
  /**
   * Options for a `commonLookup` field, by the form config's `i18nKey`. Values
   * are the raw i18n object KEYS (always strings); this module re-types them per
   * `COMMON_LOOKUP_VALUE_TYPES` into what the field actually stores.
   */
  getCommonLookupOptions: (
    i18nKey: string
  ) => { value: string; label: string }[];
  /** Closed-option custom attributes, keyed by bare schema path. */
  customAttributeOptionFields: Map<string, CustomAttributeOptionField>;
}

const NO_OPTION_SOURCES: DescriptorOptionSources = {
  getRatingOptions: () => [],
  getCommonLookupOptions: () => [],
  customAttributeOptionFields: new Map(),
};

/**
 * `displayType` is OPTIONAL on a form-config field and every other consumer
 * treats "absent" as `text`. Prefill cannot: across the register form configs,
 * "absent" covers user/group pickers (`Owners` on impact, `Owner` on control
 * group, `approvedBy`/`requestedBy`, `CompletedBy`), radio booleans
 * (`requireReattestation`), selects (`AppetiteType`, `Adherence`,
 * `attestationTimeLimit`), rich-text editors (`Content`, `RatingGuidance`),
 * numeric inputs (`Cost`, `CostValue`) and entity references (`*Id` / `*Ids`,
 * `linkedDocuments`) just as much as it covers `Title`.
 *
 * So untyped fields are FAIL-CLOSED: a field with no `displayType` is published
 * only if it appears below. Everything else untyped is skipped, which means a
 * field newly added to a shared config is never silently prefillable — the cost
 * of omission is "not prefillable yet", the cost of the old default was writing
 * a bare string into a relational or boolean field.
 *
 * Membership is decided by the CONTROL that renders the field: every key here is
 * rendered by `ControlledInput` / `ControlledTextarea` / `ControlledAutosuggest`
 * / `ControlledLinkInput` (all plain-string values) in a register form. Add a key
 * only after checking that, or give the field an explicit `displayType` in its
 * form config instead — which is the better fix.
 */
const UNTYPED_TEXT_FIELD_KEYS = new Set([
  // Shared across most registers.
  'Title',
  'title', // third_party canonically lowercases its standard field ids
  'Name',
  'Description',
  'Summary',
  // document / document_file
  'Version',
  'Link',
  'ReasonForReview',
  // internal_audit_entity
  'BusinessArea',
  // asset
  'SerialNumber',
  'Location',
  'DepreciationMethod',
  'AssetType',
  // indicator / indicator_result
  'TargetValueTxt',
  'Unit',
  // attestation_cycle
  'attestationPromptText',
  // third_party
  'address',
  'cityTown',
  'postcode',
  'country',
  'companyName',
  'contactName',
  'primaryContactName',
  // NOT listed, though they are plain text inputs: `contactEmail`,
  // `companyDomain` and `companiesHouseNumber`. These are externally verifiable
  // identifiers — a fabricated value looks authoritative, reads as plausible in
  // the proposal card, and is costly to detect once saved. Nobody needs a model
  // to draft them. Keep them out even though the type is safe.
]);

/**
 * The form-value TYPE behind each `commonLookup` i18n key.
 *
 * A `commonLookup` field's options come from an i18n object, so its option keys
 * are always strings — but the control that renders the field decides what the
 * form actually stores, and the three cases disagree:
 *
 *  - `yesOrNo` / `issues.isExternalIssue` → `ControlledBooleanRadioGroup`, whose
 *    transform turns `'true'` into a real `boolean`.
 *  - `tiers` → `ControlledRadioGroup<_, number>`, whose transform `parseInt`s.
 *  - the rest → `ControlledSelect` / identity-transform `ControlledRadioGroup`,
 *    which store the string.
 *
 * So unlike a `rating` — where the taxonomy's own value type IS the stored type,
 * because the control is chosen to match it — a `commonLookup` key needs its
 * type stated. Writing `'true'` into a boolean column, or `'1'` into a numeric
 * one, is exactly the corruption AID-240 §6 refused to risk.
 *
 * FAIL-CLOSED, like `UNTYPED_TEXT_FIELD_KEYS`: a key absent here is not
 * prefillable. To add one, find the control that renders the field and read its
 * `transform.output` — do not infer the type from how the values look. Absent
 * today for that reason: `policy.types`, `assessmentActivities.type`,
 * `assessmentActivities.status`, `assessmentResults.controlTypesCased`.
 */
const COMMON_LOOKUP_VALUE_TYPES: Partial<
  Record<string, 'boolean' | 'number' | 'string'>
> = {
  // ControlledBooleanRadioGroup → boolean
  yesOrNo: 'boolean',
  'issues.isExternalIssue': 'boolean',
  // ControlledRadioGroup<_, number>, output: parseInt → number
  tiers: 'number',
  // ControlledSelect / identity-transform ControlledRadioGroup → string
  statuses: 'string',
  treatments: 'string',
  'controls.type': 'string',
  'consequences.types': 'string',
  'consequences.costType': 'string',
  issueTypes: 'string',
  'obligations.fields.types': 'string',
};

/**
 * Re-type one `commonLookup` option key into the value its field stores.
 * `undefined` when the key cannot be represented in that type, so the option is
 * dropped rather than written as something the field cannot hold.
 */
const toCommonLookupValue = (
  key: string,
  valueType: 'boolean' | 'number' | 'string'
): boolean | number | string | undefined => {
  if (valueType === 'string') {
    return key;
  }
  if (valueType === 'boolean') {
    return key === 'true' ? true : key === 'false' ? false : undefined;
  }

  const parsed = Number(key);

  return key.trim() !== '' && Number.isFinite(parsed) ? parsed : undefined;
};

const isSupportedType = (displayType: string): boolean =>
  (SUPPORTED_DISPLAY_TYPES as readonly string[]).includes(displayType);

/**
 * Resolve the option set for a closed-option form-config field, or `undefined`
 * when the field is not one.
 *
 * Returns an EMPTY array (not `undefined`) when the field IS closed-option but
 * its options did not resolve, so the caller can tell "not a closed set" from
 * "closed set we could not resolve" and fail closed on the second.
 */
const resolveConfigOptions = (
  displayType: NonNullable<FormConfig[string]['displayType']>,
  sources: DescriptorOptionSources
): DescriptorFieldOption[] | undefined => {
  // `ratingKey` / `i18nKey` are i18next `KeyPrefix` types, which include
  // `undefined`. An option field with no key resolves to no options, which the
  // caller treats as "not prefillable" — the fail-closed branch, not a crash.
  if (displayType.displayType === 'rating') {
    return displayType.ratingKey
      ? sources.getRatingOptions(displayType.ratingKey)
      : [];
  }
  if (displayType.displayType === 'commonLookup') {
    const valueType = displayType.i18nKey
      ? COMMON_LOOKUP_VALUE_TYPES[displayType.i18nKey]
      : undefined;
    if (!valueType || !displayType.i18nKey) {
      return [];
    }

    return sources
      .getCommonLookupOptions(displayType.i18nKey)
      .flatMap(({ value, label }) => {
        const typed = toCommonLookupValue(value, valueType);

        return typed === undefined ? [] : [{ value: typed, label }];
      });
  }

  return undefined;
};

/**
 * Build the prefillable-field descriptor for the active form: the shared form
 * config intersected with the fields actually mounted (`allFieldIds` from
 * `RiskSmartFormProvider`), filtered to the display types prefill can write.
 *
 * Pure — the publisher supplies the config, the live field ids, the set of
 * required field ids, and (AID-393) the resolvers for closed option sets. A
 * field with no `displayType` is emitted only if it is an explicitly-known text
 * field (see `UNTYPED_TEXT_FIELD_KEYS`).
 *
 * `optionSources` defaults to resolving NOTHING, which keeps the fail-closed
 * guarantee honest: a caller that forgets to wire the resolvers loses
 * closed-option prefill rather than publishing those fields as unconstrained.
 */
export const buildActiveFormDescriptor = (
  formConfig: FormConfig,
  allFieldIds: string[],
  requiredFieldIds: Set<string>,
  optionSources: DescriptorOptionSources = NO_OPTION_SOURCES
): ActiveFormDescriptorField[] => {
  const descriptor: ActiveFormDescriptorField[] = [];

  for (const fieldId of allFieldIds) {
    // Custom attributes register under a nested path
    // (`CustomAttributeData.<path>`) with non-stable per-org keys and are absent
    // from the shared form config, so they are resolved from the org's
    // custom-attribute schema instead. Only the closed-option ones (`select` /
    // `multiselect`) are prefillable — the rest (text, date, number, files,
    // users, tags, departments) are not covered here.
    if (fieldId.startsWith(`${CUSTOM_ATTRIBUTE_PREFIX}.`)) {
      const path = fieldId.slice(CUSTOM_ATTRIBUTE_PREFIX.length + 1);
      const attribute = optionSources.customAttributeOptionFields.get(path);
      if (!attribute || attribute.options.length === 0) {
        continue;
      }

      descriptor.push({
        key: fieldId,
        label: attribute.label,
        displayType: attribute.multiple ? 'multiSelect' : 'select',
        required: requiredFieldIds.has(fieldId),
        options: attribute.options,
        ...(attribute.multiple ? { multiple: true } : {}),
      });
      continue;
    }
    // The bare prefix itself (the container object) is never a prefill target.
    if (fieldId === CUSTOM_ATTRIBUTE_PREFIX) {
      continue;
    }
    const config = formConfig[fieldId];
    // No config entry → a derived/unknown field (e.g. `ancestorContributors`).
    if (!config) {
      continue;
    }

    const declaredDisplayType = config.displayType;
    const declaredType = declaredDisplayType?.displayType;
    if (declaredType === undefined || declaredDisplayType === undefined) {
      // Untyped: prefillable only for known plain-text keys (fail-closed).
      if (!UNTYPED_TEXT_FIELD_KEYS.has(fieldId)) {
        continue;
      }
    } else if (isClosedSetDisplayType(declaredType)) {
      // A closed-option field is published only with its resolved values: a
      // rating spans numeric and string taxonomies, so without the option set
      // there is no way to write it without guessing the type (AID-240 §6).
      const options = resolveConfigOptions(declaredDisplayType, optionSources);
      if (!options || options.length === 0) {
        continue;
      }

      descriptor.push({
        key: fieldId,
        label: config.formLabel,
        displayType: declaredType,
        required: requiredFieldIds.has(fieldId),
        options,
      });
      continue;
    } else if (!isSupportedType(declaredType)) {
      continue;
    }

    const displayType = declaredType ?? 'text';

    descriptor.push({
      key: fieldId,
      label: config.formLabel,
      displayType,
      required: requiredFieldIds.has(fieldId),
    });
  }

  return descriptor;
};
